import React, { useState } from 'react';
import { AgentLayout } from '../../components/layout/AgentLayout';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Badge } from '../../components/common/Badge';
import { formatCurrency } from '../../utils/formatters';
import { Camera, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';

export const AgentValidateTicket: React.FC = () => {
  const [qrData, setQrData] = useState('');
  const [validating, setValidating] = useState(false);
  const [ticket, setTicket] = useState<any>(null);

  // Payout state
  const [payoutMethod, setPayoutMethod] = useState('CASH');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleValidate = async () => {
    if (!qrData.trim()) { toast.error('Please enter or paste QR data'); return; }
    setValidating(true); setTicket(null);
    try {
      const res = await api.post('/agent/validate-ticket', { qrData: qrData.trim() });
      setTicket(res.data.data?.ticket);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Invalid ticket');
    } finally { setValidating(false); }
  };

  const handlePayout = async () => {
    if (!ticket) return;
    setProcessing(true);
    try {
      const bankDetails = payoutMethod === 'BANK_TRANSFER' ? { bankName, accountNumber, accountName } : undefined;
      await api.post('/agent/payout', { ticketId: ticket.id, method: payoutMethod, bankDetails, notes: notes || undefined });
      toast.success('Payout processed successfully!');
      setTicket(null); setQrData(''); setNotes(''); setBankName(''); setAccountNumber(''); setAccountName('');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Payout failed');
    } finally { setProcessing(false); }
  };

  return (
    <AgentLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Validate Ticket</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Scan or paste ticket QR data to validate and process payouts</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Camera className="h-5 w-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Ticket QR Data</h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Paste the QR code data from the ticket. On mobile, use the Agent app's camera scanner.
          </p>
          <textarea value={qrData} onChange={(e) => setQrData(e.target.value)} rows={4} placeholder='Paste QR data here...'
            className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none font-mono mb-4" />
          <Button fullWidth onClick={handleValidate} isLoading={validating}>Validate Ticket</Button>
        </Card>

        {/* Result */}
        {ticket && (
          <div className="space-y-4">
            <Card>
              <div className="flex items-center gap-3 mb-4">
                {ticket.isWinner && !ticket.alreadyPaidOut ? (
                  <div className="p-2 bg-emerald-100 dark:bg-emerald-900/50 rounded-xl"><CheckCircle className="h-6 w-6 text-emerald-600" /></div>
                ) : ticket.alreadyPaidOut ? (
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-xl"><AlertCircle className="h-6 w-6 text-indigo-600" /></div>
                ) : (
                  <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-xl"><XCircle className="h-6 w-6 text-red-600" /></div>
                )}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {ticket.alreadyPaidOut ? 'Already Paid Out' : ticket.isWinner ? 'WINNER!' : 'Not a Winner'}
                  </h3>
                  <Badge variant={ticket.status === 'WON' ? 'success' : ticket.status === 'LOST' ? 'danger' : 'default'}>{ticket.status}</Badge>
                </div>
              </div>
              <div className="space-y-2">
                {[
                  ['Ticket #', ticket.ticketNumber],
                  ['Raffle', ticket.raffle?.name],
                  ['Customer', `${ticket.customer?.firstName} ${ticket.customer?.lastName}`],
                  ['Phone', ticket.customer?.phone || '—'],
                  ['Prize', ticket.prizeAmount ? formatCurrency(ticket.prizeAmount, ticket.raffle?.currency || 'USD') : '—'],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between py-2 border-b border-gray-50 dark:border-gray-800 last:border-0">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{value}</span>
                  </div>
                ))}
              </div>
            </Card>

            {ticket.isWinner && !ticket.alreadyPaidOut && (
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Process Payout</h3>
                <Select label="Payout Method" value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value)}
                  options={[{ value: 'CASH', label: '💵 Cash' }, { value: 'BANK_TRANSFER', label: '🏦 Bank Transfer' }]} />
                {payoutMethod === 'BANK_TRANSFER' && (
                  <div className="space-y-3 mt-3">
                    <Input label="Bank Name" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. First Bank" />
                    <Input label="Account Number" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="0123456789" />
                    <Input label="Account Name" value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="John Doe" />
                  </div>
                )}
                <Input label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes..." />
                <Button fullWidth onClick={handlePayout} isLoading={processing} className="mt-4">
                  Confirm Payout — {formatCurrency(ticket.prizeAmount, ticket.raffle?.currency || 'USD')}
                </Button>
              </Card>
            )}
          </div>
        )}
      </div>
    </AgentLayout>
  );
};
